import { queryOptions, UseMutationOptions } from '@tanstack/react-query'
import { CustomQueryOptions } from '@/common/types_and_interfaces.ts'
import {
  acceptRejectProviderItem,
  getPaginatedProviders,
  getPreviewSettings,
  getProviderDetail,
  getProviderItemCategories,
  importProviders,
  PaginatedProviders,
  updateSchoolApplication,
} from './requests'
import {
  ProviderDetail,
  ProviderPreviewSettings,
} from '@/schemas/provider.schema'
import {
  ImportProvidersResponse,
  ProviderAcceptRejectResponse,
  ProviderItemCategoryList,
  UpdateSchoolApplicationPayload,
} from '@/queries/providers/schema.ts'

export const ProviderQueryKeys = {
  list_all: ['providers'],
  importProviders: ['import_providers'],
  paginateProviders: (
    program_id: number,
    recordsPerPage: number,
    currentPage: number,
    search?: string | undefined,
    sort?: { [key: string]: 'asc' | 'desc' },
    status_in?: string
  ) => [
    ...ProviderQueryKeys.list_all,
    program_id,
    recordsPerPage,
    currentPage,
    search,
    sort,
    status_in,
  ],
  details_all: ['provider_detail'],
  details: (program_id: number, provider_id: string | number) => [
    ...ProviderQueryKeys.details_all,
    provider_id,
    program_id,
  ],
  previewSettings: (provider_id: string, program_id: number) => [
    'provider_preview_settings',
    provider_id,
    program_id,
  ],
  providerItemCategories: (provider_id: number, program_id: number) => [
    'provider_item_categories',
    provider_id,
    program_id,
  ],
  updateSchoolApplication: (
    provider_id: number,
    school_application_id: number
  ) => ['update_school_application', provider_id, school_application_id],
} as const

export class ProviderQueries {
  static getPaginatedProviders(
    program_id: number,
    recordsPerPage: number,
    currentPage: number,
    search: string = '',
    sort?: { [key: string]: 'asc' | 'desc' },
    status_in?: string,
    options: CustomQueryOptions<PaginatedProviders> = {}
  ) {
    return queryOptions({
      queryKey: ProviderQueryKeys.paginateProviders(
        program_id,
        recordsPerPage,
        currentPage,
        search,
        sort,
        status_in
      ),
      queryFn: () =>
        getPaginatedProviders(
          program_id,
          currentPage,
          recordsPerPage,
          search,
          sort,
          status_in
        ),
      ...options,
    })
  }
  public static getProviderById(
    program_id: number,
    provider_id: string | number,
    options: CustomQueryOptions<ProviderDetail> = {}
  ) {
    return {
      queryKey: ProviderQueryKeys.details(program_id, provider_id),
      queryFn: () => getProviderDetail(program_id, provider_id),
      ...options,
    }
  }
  public static getProviderPreviewSettings(
    program_id: number,
    provider_id: string,
    options: CustomQueryOptions<ProviderPreviewSettings> = {}
  ) {
    return {
      queryKey: ProviderQueryKeys.previewSettings(provider_id, program_id),
      queryFn: () => getPreviewSettings(program_id, provider_id),
      ...options,
    }
  }

  public static getProviderItemCategories(
    provider_id: number,
    program_id: number,
    options: CustomQueryOptions<ProviderItemCategoryList> = {}
  ) {
    return queryOptions({
      queryKey: ProviderQueryKeys.providerItemCategories(
        provider_id,
        program_id
      ),
      queryFn: () => getProviderItemCategories(provider_id, program_id),
      ...options,
    })
  }

  public static acceptRejectProviderItem(
    providerId: number,
    providerItemId: number,
    programId: number,
    status: 0 | 1 | 2,
    justification?: string,
    options: CustomQueryOptions<ProviderAcceptRejectResponse> = {}
  ) {
    return {
      mutationKey: ['accept_reject_provider_item', providerId, providerItemId],
      mutationFn: () =>
        acceptRejectProviderItem(
          providerId,
          providerItemId,
          programId,
          status,
          justification
        ),
      ...options,
    }
  }

  public static updateSchoolApplication(
    provider_id: number,
    school_application_id: number,
    program_id: number
  ): UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    Partial<UpdateSchoolApplicationPayload>,
    unknown
  > {
    return {
      mutationKey: ProviderQueryKeys.updateSchoolApplication(
        provider_id,
        school_application_id
      ),
      mutationFn: (payload) =>
        updateSchoolApplication(
          provider_id,
          school_application_id,
          program_id,
          payload
        ),
    }
  }

  public static importProviders(options?: {
    onSuccess?: (response: ImportProvidersResponse) => Promise<void>
    onError?: (error: Error) => Promise<void>
  }) {
    return {
      mutationKey: ProviderQueryKeys.importProviders,
      mutationFn: (params: { file: File; programId: number }) => {
        const { file, programId } = params
        return importProviders(file, programId)
      },
      ...options,
    }
  }
}
